import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, Grid, HStack, Text, useBanner } from '@tq/ui-components';

import { useInviteConsultee, InviteConsulteeRequest } from 'api';
import { BANNER_TIMEOUT } from 'config';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { showRemoveUserModalState, removeUserState } from 'store';
import { ApplicationUser, ValueLabelResponse } from 'types';
import { TableCellBadges } from 'components';

import { ManageTeamActionMenu } from '../ManageTeamActionMenu';

interface ManageTeamRowProps {
  data: ApplicationUser;
  columnWidths: string;
  isAdmin?: boolean;
  setEditable: (editable: boolean) => void;
}

export const ViewTeamRow: React.FC<ManageTeamRowProps> = ({
  data,
  columnWidths,
  isAdmin = false,
  setEditable,
}) => {
  const banner = useBanner();
  const { mutate: inviteConsultee } = useInviteConsultee();
  const { t } = useTranslation('manage_team');
  const isMobile = useIsMobile();

  const setShowManageTeamRemoveUserModal = useSetRecoilState(showRemoveUserModalState);
  const setRemoveUser = useSetRecoilState(removeUserState);

  const onEditItem = () => {
    setEditable(true);
  };

  const onRemoveItem = () => {
    setShowManageTeamRemoveUserModal(true);
    setRemoveUser(data);
  };

  const onResendInvite = () => {
    inviteConsultee(
      {
        name: data.name,
        email: data.email,
        permissionId: data.permission?.value ?? 0,
        teamIds: data.teams?.map((response: ValueLabelResponse) => response.value) ?? [],
      } as InviteConsulteeRequest,
      {
        onSuccess: () => {
          banner({
            id: 'resend-invite-banner-success',
            title: t('invite_sent_success', 'Invite sent'),
            duration: BANNER_TIMEOUT,
          });
        },
        onError: () => {
          banner({
            id: 'resend-invite-banner-fail',
            title: t('invite_sent_failed', 'Failed to resend invite. Please try again.'),
            duration: BANNER_TIMEOUT,
            status: 'error',
          });
        },
      }
    );
  };

  const getBaseTemplateAreas = isAdmin
    ? `'name menu'
        'email registered'`
    : `'name menu'
        'email registered'
        'team permissions'`;
  const getLgTemplateAreas = isAdmin
    ? `'name email registered menu'`
    : `'name email team permissions registered menu'`;

  return (
    <Grid
      templateAreas={{
        base: getBaseTemplateAreas,
        lg: getLgTemplateAreas,
      }}
      gridTemplateColumns={{ base: 'max-content', lg: columnWidths }}
      alignItems='baseline'
      p={{
        base: '.625rem .625rem .5rem 1rem',
        lg: '.625rem .625rem 1rem 1rem',
      }}
      bg={COLOURS.gray[100]}
      border={`1px solid ${COLOURS.gray[300]}`}
      borderRadius='.25rem'
    >
      <Text gridArea='name' size='sm' fontWeight='bold' mb={{ base: '1rem', lg: 0 }}>
        {data.name}
      </Text>
      <Text gridArea='email' size='sm' fontWeight='bold' mb={{ base: '1rem', lg: 0 }}>
        {data.email}
      </Text>
      {!isAdmin && (
        <>
          <TableCellBadges items={data.teams} />
          <Text
            gridArea='permissions'
            size='sm'
            fontWeight='bold'
            justifySelf={{ base: 'end', lg: 'start' }}
          >
            {data.permission?.label}
          </Text>
        </>
      )}
      <HStack
        gridArea='registered'
        mb={{ base: '1rem', lg: 0 }}
        justifySelf={{ base: 'end', lg: 'start' }}
      >
        {isMobile && <Text size='sm'>{t('registered_value', 'Registered')}</Text>}
        <Text size='sm' fontWeight='bold' display='inline'>
          {data.isRegistered ? 'Yes' : 'No'}
        </Text>
      </HStack>
      <Box gridArea='menu' justifySelf='end' alignSelf='baseline'>
        <ManageTeamActionMenu
          onEdit={onEditItem}
          onRemove={onRemoveItem}
          onResendInvite={onResendInvite}
        />
      </Box>
    </Grid>
  );
};
